import { useNavigate } from 'react-router-dom';
import type { Manga } from '@/mocks/mangaData';

interface MangaCardProps {
  manga: Manga;
}

export default function MangaCard({ manga }: MangaCardProps) {
  const navigate = useNavigate();

  const getCategoryGradient = (category: string) => {
    switch (category) {
      case 'korean': return 'from-primary-500 to-primary-700';
      case 'japanese': return 'from-secondary-500 to-secondary-700';
      case 'chinese': return 'from-accent-500 to-accent-700';
      default: return 'from-primary-500 to-primary-700';
    }
  };

  const handleClick = () => {
    navigate(`/manga/${manga.id}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <article
      className="group cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
        <img
          src={manga.coverUrl}
          alt={`${manga.title} - ${manga.categoryLabel}漫画`}
          title={`${manga.title} - ${manga.categoryLabel}漫画推荐`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />

        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full bg-gradient-to-r ${getCategoryGradient(manga.category)} text-background-50 text-xs font-medium`}>
          {manga.categoryLabel}
        </div>

        {manga.status === '已完结' && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-secondary-500 text-background-50 text-xs font-medium">
            完结
          </div>
        )}

        <div className="absolute bottom-2 left-2 right-2">
          <div className="flex items-center gap-2 text-background-50/90 text-xs">
            <div className="flex items-center gap-0.5">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-star-fill text-accent-500"></i>
              </div>
              <span className="font-semibold">{manga.rating}</span>
            </div>
            <span className="text-background-50/60">|</span>
            <span>{manga.ratingCount.toLocaleString()}人评</span>
          </div>
        </div>
      </div>

      <h3 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-500 transition-colors mb-1">
        {manga.title}
      </h3>

      <p className="text-xs text-foreground-600">{manga.author}</p>

      <div className="flex items-center flex-wrap gap-1 mt-1.5">
        {manga.tags.slice(0, 3).map((tag) => (
          <span key={tag} className="px-1.5 py-0.5 bg-background-100 text-foreground-600 text-xs rounded">
            {tag}
          </span>
        ))}
      </div>
    </article>
  );
}